import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Truck, Package, DollarSign, FileText, CheckCircle, Clock,
  MapPin, Phone, Mail, User, TrendingUp, Calendar, CreditCard,
  ChevronRight, LogOut, Home, AlertCircle, MessageCircle, PhoneIncoming, 
  Video, X, Download
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { ChatInterface } from '@/components/ChatInterface';
import { useWebRTCCall, CallInterface } from '@/components/CallInterface';
import CannotCoverButton from '@/components/CannotCoverButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverDashboard = () => {
  const [driver, setDriver] = useState(null);
  const [loads, setLoads] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [driverEmail, setDriverEmail] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  
  // WebRTC for receiving calls
  const {
    callState,
    currentCall,
    incomingCall,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo
  } = useWebRTCCall(driver?.id, driver?.name, 'driver');
  
  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('App installed! You can now receive calls and messages.');
    }
    setDeferredPrompt(null);
    setShowInstallBtn(false);
  };

  const lookupDriver = async () => {
    if (!driverEmail) {
      setLoginError('Please enter your email');
      return;
    }
    
    setLoading(true);
    setLoginError('');
    
    try {
      // Search for driver by email
      const response = await axios.get(`${API}/drivers?search=${encodeURIComponent(driverEmail)}&limit=1`);
      
      if (response.data && response.data.length > 0) {
        const foundDriver = response.data[0];
        setDriver(foundDriver);
        setIsLoggedIn(true);
        localStorage.setItem('driverEmail', driverEmail);
        localStorage.setItem('driverId', foundDriver.id);
        await fetchDriverData(foundDriver.id);
      } else {
        setLoginError('Driver not found. Please check your email.');
      }
    } catch (error) {
      console.error('Error looking up driver:', error);
      setLoginError('Failed to find driver. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverData = async (driverId) => {
    try {
      // Fetch driver's assigned loads
      const loadsResponse = await axios.get(`${API}/loads?assigned_driver_id=${driverId}&limit=50`);
      setLoads(loadsResponse.data || []);
      
      // Fetch driver's invoices
      const invoicesResponse = await axios.get(`${API}/invoices?driver_id=${driverId}&limit=50`);
      setInvoices(invoicesResponse.data || []);
      
      // Fetch driver's contracts
      const contractsResponse = await axios.get(`${API}/contracts?driver_id=${driverId}&limit=10`);
      setContracts(contractsResponse.data || []);
      
      // Calculate stats
      const totalEarnings = loadsResponse.data?.reduce((sum, load) => sum + (load.rate || 0), 0) || 0;
      const totalFees = invoicesResponse.data?.reduce((sum, inv) => sum + (inv.fee_amount || 0), 0) || 0;
      const pendingFees = invoicesResponse.data?.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + inv.fee_amount, 0) || 0;
      
      setStats({
        totalLoads: loadsResponse.data?.length || 0,
        completedLoads: loadsResponse.data?.filter(l => l.status === 'completed').length || 0,
        totalEarnings,
        totalFees,
        pendingFees,
        netEarnings: totalEarnings - totalFees
      });
    } catch (error) {
      console.error('Error fetching driver data:', error);
    }
  };

  useEffect(() => {
    // Check if driver was previously logged in
    const savedEmail = localStorage.getItem('driverEmail');
    const savedId = localStorage.getItem('driverId');
    
    if (savedEmail && savedId) {
      setDriverEmail(savedEmail);
      // Auto-login
      axios.get(`${API}/drivers/${savedId}`)
        .then(response => {
          setDriver(response.data);
          setIsLoggedIn(true);
          fetchDriverData(savedId);
        })
        .catch(() => {
          localStorage.removeItem('driverEmail');
          localStorage.removeItem('driverId');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('driverEmail');
    localStorage.removeItem('driverId');
    setDriver(null);
    setIsLoggedIn(false);
    setDriverEmail('');
    setLoads([]);
    setInvoices([]);
    setContracts([]);
    setStats(null);
  };

  const initiatePayment = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/payment`, {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-amber-500 bg-amber-500/15',
      assigned: 'text-blue-500 bg-blue-500/15',
      in_transit: 'text-purple-500 bg-purple-500/15',
      completed: 'text-emerald-500 bg-emerald-500/15',
      paid: 'text-emerald-500 bg-emerald-500/15',
      draft: 'text-slate-400 bg-slate-500/15',
      sent: 'text-amber-500 bg-amber-500/15',
      signed: 'text-emerald-500 bg-emerald-500/15'
    };
    return colors[status] || 'text-slate-400 bg-slate-500/15';
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md glass-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Truck className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <CardTitle className="text-2xl font-black font-heading text-white">Driver Portal</CardTitle>
            <p className="text-slate-400 text-sm mt-2">Access your loads, invoices, and contracts</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Your Email</label>
              <Input
                type="email"
                placeholder="Enter your registered email"
                value={driverEmail}
                onChange={(e) => setDriverEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && lookupDriver()}
                className="bg-slate-800/50"
                data-testid="driver-email-input"
              />
            </div>
            {loginError && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {loginError}
              </div>
            )}
            <Button 
              onClick={lookupDriver} 
              disabled={loading}
              className="w-full glow-button"
              data-testid="driver-login-btn"
            >
              {loading ? 'Looking up...' : 'Access Dashboard'}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-4">
              Use the email registered with Tempus Dispatch
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Incoming Call Modal */}
      {incomingCall && callState === 'ringing' && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm bg-slate-900 border-slate-700">
            <CardContent className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                <PhoneIncoming className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">Incoming Call</h3>
              <p className="text-slate-400 mb-6">{incomingCall.caller_name || 'Dispatch Center'}</p>
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={() => rejectCall(incomingCall.id)}
                  className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
                >
                  <X className="w-6 h-6" />
                </Button>
                <Button 
                  onClick={() => answerCall(incomingCall.id)}
                  className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600"
                >
                  <Phone className="w-6 h-6" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Active Call UI */}
      {(callState === 'connected' || callState === 'calling') && (
        <CallInterface
          callState={callState}
          currentCall={currentCall}
          localStream={localStream}
          remoteStream={remoteStream}
          isMuted={isMuted}
          isVideoOff={isVideoOff}
          onEndCall={endCall}
          onToggleMute={toggleMute}
          onToggleVideo={toggleVideo}
        />
      )}
      
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Truck className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm truncate max-w-[150px]">{driver?.name}</p>
              <p className="text-slate-500 text-xs">{driver?.mc_number || driver?.dot_number || 'Driver'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Install App Button */}
            {showInstallBtn && (
              <Button 
                onClick={handleInstall} 
                size="sm" 
                variant="outline"
                className="border-green-500 text-green-400"
              >
                <Download className="w-4 h-4 mr-1" />
                Install
              </Button>
            )}
            {/* Chat Button */}
            <Button variant="ghost" size="sm" onClick={() => setChatOpen(true)} className="text-slate-400">
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Chat Interface */}
      <ChatInterface
        userId={driver?.id}
        userName={driver?.name}
        userType="driver"
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
      />

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6 pb-24">
        {/* Stats Cards */}
        {activeTab === 'home' && stats && (
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-3">
              <Card className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Net Earnings</p>
                      <p className="text-xl font-black text-white">${stats.netEarnings.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Pending Fees</p>
                      <p className="text-xl font-black text-white">${stats.pendingFees.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Package className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Loads</p>
                      <p className="text-lg font-bold text-white">{stats.totalLoads}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Completed</p>
                    <p className="text-lg font-bold text-emerald-400">{stats.completedLoads}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loads Tab */}
        {activeTab === 'home' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-400" />
              My Loads
            </h2>
            {loads.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No loads assigned yet</p>
                </CardContent>
              </Card>
            ) : (
              loads.slice(0, 5).map((load) => (
                <Card key={load.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(load.status)}`}>
                        {load.status}
                      </span>
                      <span className="text-emerald-400 font-bold">${load.rate?.toLocaleString()}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-slate-400 text-xs">Pickup</p>
                          <p className="text-white">{load.pickup_address}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-slate-400 text-xs">Delivery</p>
                          <p className="text-white">{load.delivery_address}</p>
                        </div>
                      </div>
                    </div>
                    {['assigned', 'in_transit', 'accepted'].includes(load.status) &&
                      (load.assigned_driver_email || '').toLowerCase() === (driver?.email || '').toLowerCase() && (
                        <div className="mt-3 pt-3 border-t border-slate-700/50 flex justify-end">
                          <CannotCoverButton
                            loadId={load.id}
                            driverEmail={driver?.email || ''}
                            onCovered={() => {
                              toast.success('Logged — backup carrier dispatched');
                              window.location.reload();
                            }}
                          />
                        </div>
                      )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-400" />
              My Invoices
            </h2>
            {invoices.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No invoices yet</p>
                </CardContent>
              </Card>
            ) : (
              invoices.map((invoice) => (
                <Card key={invoice.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                      <span className="text-white font-bold text-lg">${invoice.fee_amount?.toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-slate-400 mb-3">
                      <p>Load Rate: ${invoice.load_rate?.toLocaleString()}</p>
                      <p>Dispatch Fee: {(invoice.fee_percentage * 100).toFixed(0)}%</p>
                    </div>
                    {invoice.status === 'pending' && (
                      <Button 
                        onClick={() => initiatePayment(invoice.id)}
                        className="w-full glow-button"
                        size="sm"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay Now
                      </Button>
                    )}
                    {invoice.status === 'paid' && (
                      <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Paid
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Contracts Tab */}
        {activeTab === 'contracts' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              My Contracts
            </h2>
            {contracts.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-6 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No contracts yet</p>
                </CardContent>
              </Card>
            ) : (
              contracts.map((contract) => (
                <Card key={contract.id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-semibold">Dispatch Agreement</span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusColor(contract.status)}`}>
                        {contract.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-400">
                      <p>Fee: {(contract.fee_percentage * 100).toFixed(0)}%</p>
                      <p>Created: {new Date(contract.created_at).toLocaleDateString()}</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && driver && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              My Profile
            </h2>
            <Card className="glass-card">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{driver.name}</p>
                    <p className="text-slate-400 text-sm">{driver.home_base || 'Owner-Operator'}</p>
                  </div>
                </div>
                <div className="space-y-3 pt-4 border-t border-slate-700">
                  {driver.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{driver.email}</span>
                    </div>
                  )}
                  {driver.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{driver.phone}</span>
                    </div>
                  )}
                  {driver.mc_number && (
                    <div className="flex items-center gap-3 text-sm">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">MC: {driver.mc_number}</span>
                    </div>
                  )}
                  {driver.dot_number && (
                    <div className="flex items-center gap-3 text-sm">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">DOT: {driver.dot_number}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-slate-700/50">
        <div className="max-w-lg mx-auto flex items-center justify-around py-2">
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'invoices', icon: DollarSign, label: 'Invoices' },
            { id: 'contracts', icon: FileText, label: 'Contracts' },
            { id: 'profile', icon: User, label: 'Profile' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'text-blue-400 bg-blue-500/10' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard;
